import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PanelRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPhone } from "@/lib/format";
import type { Inbox } from "@/types/inbox";

interface InboxHeaderProps {
  inboxId: number;
  inbox?: Inbox;
  onToggleDetail?: () => void;
  onStatusChange?: (status: "open" | "pending" | "resolved") => void;
}

const statusLabels: Record<string, string> = {
  open: "Aberto",
  pending: "Pendente",
  resolved: "Resolvido",
};

const channelIcons: Record<string, React.ReactNode> = {
  whatsapp: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="h-4 w-4 text-green-500"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    </svg>
  ),
};

export function InboxHeader({
  inboxId,
  inbox,
  onToggleDetail,
  onStatusChange,
}: InboxHeaderProps) {
  const contactName = inbox?.contact_name || `Conversa #${inboxId}`;
  const contactPhone = inbox?.contact_phone;
  const channelType = inbox?.channel_type;
  const status = inbox?.status;

  return (
    <div className="flex h-14 items-center justify-between border-b px-4">
      <div className="flex items-center gap-3 min-w-0">
        {channelType && channelIcons[channelType] && (
          <span className="shrink-0">{channelIcons[channelType]}</span>
        )}
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">{contactName}</h2>
          {contactPhone && (
            <p className="truncate text-xs text-muted-foreground">
              {formatPhone(contactPhone)}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {status && onStatusChange && (
          <Select
            value={status}
            onValueChange={(v) =>
              onStatusChange(v as "open" | "pending" | "resolved")
            }
          >
            <SelectTrigger size="sm" className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        value === "open" && "bg-green-500",
                        value === "pending" && "bg-amber-500",
                        value === "resolved" && "bg-slate-400",
                      )}
                    />
                    {label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {onToggleDetail && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onToggleDetail}
            title="Detalhes do contato"
          >
            <PanelRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
